import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import { styled } from "@mui/material";
import LoadingButton from "_shared/_views/loading/loading-button";
import Guid from "_shared/guids/guid";
import {
  useCreateIndividualBookmarkMutation,
  useDeleteIndividualBookmarkMutation,
  useGetAllIndividualBookmarksQuery,
} from "marketplace/bookmarks/_api/bookmark-api";
import { useSnackbar } from "notistack";

const StyledBookmarkButton = styled(LoadingButton)<{ selected: boolean }>(({ selected, theme }) => ({
  backgroundColor: selected ? theme.palette.primary.main : undefined,
  color: selected ? theme.palette.primary.contrastText : undefined,
  marginBottom: theme.spacing(1),
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(24),
  whiteSpace: "nowrap",
  "&:hover": {
    backgroundColor: selected ? theme.palette.primary.main : undefined,
    color: selected ? theme.palette.primary.contrastText : undefined,
  },
}));

export interface IndividualBookmarkButtonProps {
  className?: string;
  individualId?: Guid;
}

export default function IndividualBookmarkButton(props: Readonly<IndividualBookmarkButtonProps>) {
  const { className, individualId } = props;

  const { enqueueSnackbar } = useSnackbar();

  const { data: individualBookmarks, isLoading: bookmarksLoading } = useGetAllIndividualBookmarksQuery();
  const [createIndividualBookmark, { isLoading: isCreating }] = useCreateIndividualBookmarkMutation();
  const [deleteIndividualBookmark, { isLoading: isDeleting }] = useDeleteIndividualBookmarkMutation();

  const existingBookmark = individualBookmarks?.find((b) => b.individualId === individualId?.value);
  const isLoading = bookmarksLoading || isCreating || isDeleting;

  async function handleBookmarkClick() {
    try {
      if (existingBookmark) {
        await deleteIndividualBookmark({ bookmarkId: existingBookmark.id }).unwrap();
        enqueueSnackbar("Removed bookmark", { variant: "info" });
      } else {
        if (!individualId) throw new Error("Bookmark Individual: Individual ID is undefined");
        await createIndividualBookmark({ individualId: individualId.value }).unwrap();
        enqueueSnackbar("Added bookmark", { variant: "info" });
      }
    } catch (err) {
      console.error(err);
      enqueueSnackbar("Couldn't save bookmark", { variant: "error" });
    }
  }

  return (
    <StyledBookmarkButton
      className={className}
      selected={existingBookmark !== undefined}
      variant="outlined"
      aria-label="Bookmark"
      loading={isLoading}
      startIcon={<BookmarkBorderIcon />}
      disableElevation
      disabled={!individualId}
      onClick={handleBookmarkClick}
    >
      {existingBookmark ? "Bookmarked" : "Bookmark"}
    </StyledBookmarkButton>
  );
}
